<script setup>
import { gApp } from '../Globals.js'
import { ref, onUnmounted, computed, watch } from 'vue'
import BasicModal from './utils/BasicModal.vue'
import CloseButton from './utils/CloseButton.vue'
import AdminOnly from './AdminOnly.vue'
import { getColorFromUsername } from '../SharedUtils'

let showProfileInfo = ref(false);
let showToolsDropdown = ref(false);
let toolsDropdownTimeout = null;

let sendFeedbackModal = ref(null);
let getHelpModal = ref(null);
//let updateModal = ref(null);
let feedbackText = ref("");
let helpText = ref("");

function showToolsDropdownDelayed() {
  if (toolsDropdownTimeout) {
    clearTimeout(toolsDropdownTimeout);
  }
  showToolsDropdown.value = true;
}

function hideToolsDropdownDelayed() {
  toolsDropdownTimeout = setTimeout(() => {
    showToolsDropdown.value = false;
  }, 150);
}

async function signOutUser() {
  showProfileInfo.value = false;
  await gApp.signOutUser();
}

function goToProfile() {
  showProfileInfo.value = false;
  gApp.router.push('/account');
}

let feedbackTemplate = `I wish HeatWise had these features:


I hit these bugs / problems:


Other feedback:

`

let helpTemplate = ``

// Note - cannot be used directly in the template
const version = __APP_VERSION__

const isStaging = import.meta.env.MODE === 'staging';

function openSendFeedback() {
  feedbackText.value = feedbackTemplate;
  sendFeedbackModal.value.showModal();
}

function openGetHelp() {
  helpText.value = helpTemplate;
  getHelpModal.value.showModal();
}

function sendFeedback() {
  gApp.sendDeveloperFeedback('User Feedback', feedbackText.value);
  feedbackText.value = "";
  gApp.toast('Feedback sent!');
}

function sendHelpRequest() {
  gApp.sendDeveloperFeedback('Help Request', helpText.value);
  helpText.value = "";
  gApp.toast('Help request sent!');
}

onUnmounted(() => {
})

</script>

<template>
  <div class="App">
    <div class="AppHeader Flex AlignCenter">
      <div class="LogoContainer">
        <img class="AppLogo mr-xs cursor-ptr" src="../assets/cpu-fan-cooling-icon.png" @click="gApp.router.push('/')"/>
      </div>
      <div class="TitleContainer Flex AlignBaseline">
        <h3 class="AppTitle cursor-ptr glow-text" @click="gApp.router.push('/')">HeatWise</h3>
        <div class="VersionBadge ml-xs">
          <p class="f-xs VersionNum">v{{version}}</p>
        </div>
        <div v-if="isStaging" class="StagingBadge ml-xs">
          <p class="f-m">STAGING</p>
        </div>
      </div>
      <p class="CurProjTitle Bold cursor-ptr" @click="gApp.router.push(gApp.proj().getBaseRoute())" v-if="gApp.proj()">
        <i class="bi-chevron-right white mr-s"></i>
        <i class="bi-box-fill pc mr-xs"></i>
        {{gApp.proj().getUiName()}}
      </p>
      <div class="TopBtns Flex GapXS AlignBaseline">
        <!-- <button v-if="gApp.proj()" @click="gApp.saveAll()">Save Project</button> -->
        <template v-if="gApp.authState.value == 'SignedIn'">
          <!-- <i class="bi-person-circle"></i> -->
          <!-- <p class="f-s MRXS">{{gApp.userInfo.email}}</p> -->
          <div class="tools-dropdown-container" 
               @mouseenter="showToolsDropdownDelayed" 
               @mouseleave="hideToolsDropdownDelayed">
            <button class="CreateBtn ToolsBtn">
              <i class="bi-tools mr-xxs"></i>Tools
            </button>
            <div v-if="showToolsDropdown" class="ToolsDropdown pos-abs right-0 pane shad-m">
              <div class="tools-dropdown-content">
                <h4 class="tools-dropdown-title">Tools</h4>
                <router-link to="/psychrometrics" class="tools-dropdown-link">
                  <i class="bi-thermometer-half mr-xs"></i>
                  Psychrometrics Calculator
                </router-link>
                <router-link to="/affinity-laws" class="tools-dropdown-link">
                  <i class="bi-sliders mr-xs"></i>
                  Affinity Laws Calculator
                </router-link>
                <router-link to="/hydronics-calculator" class="tools-dropdown-link">
                  <i class="bi-water mr-xs"></i>
                  Hydronics Calculator
                </router-link>
                <router-link to="/pipe-head-loss" class="tools-dropdown-link">
                  <i class="bi-droplet-half mr-xs"></i>
                  Pipe Head Loss Calculator
                </router-link>
                <router-link to="/ductulator" class="tools-dropdown-link">
                  <i class="bi-wind mr-xs"></i>
                  Ductulator
                </router-link>
                <router-link to="/duct-pressure-loss" class="tools-dropdown-link">
                  <i class="bi-speedometer2 mr-xs"></i>
                  Duct Pressure Loss Calculator
                </router-link>
                <h4 class="tools-dropdown-title mt-s">Resources</h4>
                <a href="https://www.heatwise-hvac.com/tutorials/" target="_blank" class="tools-dropdown-link">
                  <i class="bi-book mr-xs"></i>
                  Tutorials & Guides
                </a>
              </div>
            </div>
          </div>
          <button class="CreateBtn HelpBtn" @click="openGetHelp"><i class="bi-asterisk mr-xxs"></i>Get Help</button>
          <button class="CreateBtn FeedbackBtn mr-xxs" @click="openSendFeedback"><i class="bi-send mr-xxs"></i>Send Feedback</button>
          <div class="pos-rel">
            <div class="ProfileBtn text-btn MRXS upper hover-grow" 
                 :class="{grown: showProfileInfo}" 
                 :style="{ backgroundColor: getColorFromUsername(gApp.userInfo.email) }"
                 @click="showProfileInfo = !showProfileInfo">
              <p class="upper black f-xs">{{gApp.userInfo.email[0]}}</p>
            </div>
            <div v-if="showProfileInfo" class="ProfileDropdown pos-abs right-0 pane mt-xs shad-m">
              <CloseButton @click="showProfileInfo=false" class="float-r"/>
              <div class="profile-header">
                <div class="profile-icon-large" :style="{ backgroundColor: getColorFromUsername(gApp.userInfo.email) }">
                  <p class="upper f-m">{{gApp.userInfo.email[0]}}</p>
                </div>
                <div class="profile-info">
                  <p class="profile-title">Profile</p>
                  <p class="profile-email">{{gApp.userInfo.email}}</p>
                </div>
              </div>
              <div class="profile-actions">
                <button class="profile-manage-btn mb-s" @click="goToProfile">
                  <i class="bi-gear mr-xs"></i>Account Settings
                </button>
                <AdminOnly>
                  <router-link to="/admin-console" class="profile-manage-btn mb-s admin-console-btn" @click="showProfileInfo = false">
                    <i class="bi-shield-lock mr-xs"></i>Admin Console
                  </router-link>
                </AdminOnly>
                <button v-if="gApp.authState.value == 'SignedIn'" class="profile-signout-btn" @click="signOutUser">
                  <i class="bi-box-arrow-right mr-xs"></i>Sign Out
                </button>
              </div>
            </div>
          </div>
          <!-- <button v-if="gApp.authState.value == 'SignedIn'" class="LinkBtn SignOutBtn w-text f-xs" @click="signOutUser">Sign Out</button> -->
        </template>
      </div>
    </div>
    <div class="BodyContainer">
      <router-view></router-view>
    </div>
    <BasicModal ref="sendFeedbackModal" title="Send Feedback" doneText="Send" @onDone="sendFeedback">
      <p class="mute-header">Feedback</p>
      <p class="help-text mb-s">Fill in this form to send feedback to the developers.</p>
      <textarea v-model="feedbackText" class="mb-s w-100 FeedbackArea"/>
    </BasicModal>
    <BasicModal ref="getHelpModal" title="Get Help" doneText="Send" @onDone="sendHelpRequest">
      <p class="mute-header">Help Request</p>
      <p class="help-text mb-s">Describe what you need help with and we'll get back to you as soon as possible.</p>
      <textarea v-model="helpText" class="mb-s w-100 FeedbackArea"/>
    </BasicModal>
  </div>
</template>

<style scoped>
.App {
  /* max-width: 1200px; */
  width: 100%;
  height: 100%;
  /* padding: var(--space-m); */
  background-color: var(--main-bg);
}

.AppHeader {
  height: var(--header-height);
  background: var(--header-color);
  padding: 0 var(--space-m);
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  color: var(--w-text);
  width: 100%;
  position: fixed;
  z-index: 2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

@media print {
  /* Hide header when printing */
  .AppHeader {
    display: none;
  }
}

.LogoContainer {
  display: flex;
  align-items: center;
  position: relative;
}

.TitleContainer {
  position: relative;
  display: flex;
  align-items: baseline;
}

.CurProjTitle {
  float: left;
  /*margin-left: calc(var(--sidebar-w) - 150px);*/
  margin-left: var(--l);
  transition: transform 0.3s ease;
}

.CurProjTitle:hover {
  transform: translateY(-2px);
}

.TopBtns {
  margin-left: auto;
}

.AppLogo {
  height: 32px;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}


.AppTitle {
  color: var(--title-color);
  font-size: var(--f-l);
  font-weight: 700;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

.glow-text:hover {
  transform: translateY(-2px);
}

.VersionBadge {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 2px 8px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.VersionBadge:hover {
  background-color: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.StagingBadge {
  background-color: rgba(255, 87, 51, 0.8);
  border-radius: 12px;
  padding: 2px 8px;
  font-weight: bold;
  box-shadow: 0 1px 2px rgba(255, 87, 51, 0.3);
}

.BodyContainer {
  padding-top: var(--header-height);
  position: relative;
  z-index: 1;
  height: 100%;
}

.ProfileBtn {
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  text-align: center;
  position: relative;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.ProfileBtn:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.ProfileBtn p {
  font-weight: 700;
  font-size: var(--f-m);
}

.ProfileDropdown {
  color: var(--main-text);
  z-index: 1000;
  border-radius: var(--br-l);
  min-width: 360px;
  padding: var(--m);
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeInDown 0.3s ease-out;
}

.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: var(--m);
  padding-bottom: var(--s);
  border-bottom: 1px solid rgba(62, 85, 204, 0.1);
}

.profile-icon-large {
  height: 60px;
  width: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  margin-right: var(--m);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: var(--w-text);
  font-size: var(--f-l);
  font-weight: 700;
}

.profile-info {
  flex: 1;
}

.profile-title {
  font-size: var(--f-m);
  font-weight: 700;
  color: var(--pc);
  margin-bottom: var(--xxs);
}

.profile-email {
  font-size: var(--f-s);
  color: var(--grey-40);
  word-break: break-all;
}

.profile-actions {
  margin-top: var(--m);
}

.profile-manage-btn, .profile-signout-btn {
  display: flex;
  align-items: center;
  padding: var(--xs) var(--s);
  border-radius: var(--br-m);
  font-weight: 600;
  transition: all 0.2s ease;
  width: 100%;
}

.profile-manage-btn {
  background-color: var(--pc);
  color: white;
  border: none;
  text-decoration: none;
}

.profile-manage-btn:hover {
  background-color: var(--pc-highlight);
  color: white;
  text-decoration: none;
  transform: translateY(-2px);
}

.admin-console-btn {
  background-color: var(--warning-orange);
}

.admin-console-btn:hover {
  background-color: var(--warning-orange-focus);
}

.profile-signout-btn {
  background-color: rgba(62, 85, 204, 0.05);
  color: var(--pc);
  border: 1px solid rgba(62, 85, 204, 0.2);
}

.profile-signout-btn:hover {
  background-color: rgba(62, 85, 204, 0.1);
  transform: translateY(-2px);
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.FeedbackBtn, .HelpBtn, .ToolsBtn {
  padding: var(--xs);
  font-size: var(--f-xxs);
  text-transform: none;
  border-radius: var(--br-l);
}

.HelpBtn, .ToolsBtn {
  padding: var(--xs) var(--s);
}

.tools-dropdown-container {
  position: relative;
}

.ToolsDropdown {
  color: var(--main-text);
  z-index: 1000;
  border-radius: var(--br-l);
  min-width: 350px;
  padding: var(--m);
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: fadeInDown 0.3s ease-out;
}

.tools-dropdown-content {
  display: flex;
  flex-direction: column;
}

.tools-dropdown-title {
  font-size: var(--f-s);
  font-weight: 600;
  color: var(--pc);
  margin-bottom: var(--xs);
  padding-bottom: var(--xxs);
  border-bottom: 1px solid rgba(62, 85, 204, 0.1);
}

.tools-dropdown-link {
  display: flex;
  align-items: center;
  padding: var(--xs) var(--s);
  color: var(--pc-dark);
  text-decoration: none;
  border-radius: var(--br-s);
  font-weight: 500;
  transition: all 0.2s ease;
  margin-bottom: var(--xxs);
}

.tools-dropdown-link:hover {
  background-color: var(--pc-lightest);
  color: var(--pc);
  text-decoration: none;
  transform: translateX(4px);
}

.tools-dropdown-link i {
  color: var(--pc);
  font-size: var(--f-s);
}

.FeedbackArea {
  resize: vertical;
  height: 200px;
  font-size: var(--f-xs);
}

.VersionNum {
  color: white;
  font-weight: 600;
}

.update-content {
  text-align: center;
  padding: var(--m);
}

.update-icon {
  font-size: var(--f-xxl);
  color: var(--pc);
  margin-bottom: var(--s);
  display: block;
}

.update-message {
  font-size: var(--f-l);
  font-weight: 600;
  color: var(--pc-dark);
  margin-bottom: var(--xs);
}

</style>
